import { useCallback, useEffect, useState } from "preact/hooks";
import { useQuery } from "./db.ts";
import { getApiBase } from "./config.ts";
import { t } from "./theme.ts";
import { ErrorMessage, Loading } from "./uiComponents.tsx";
import { ClipLoader } from "react-spinners";
import { empty } from "@uri/gamla";
import { marked } from "marked";

const apiBase = getApiBase();

const callApi = (
  endpoint: string,
  token: string,
  payload: Record<string, unknown> = {},
) =>
  fetch(apiBase, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint, token, payload }),
  }).then((r) => r.json());

type Report = {
  id: string;
  content: string;
  createdAt: number;
  conversationIds: string;
  viviConversationId?: string;
  checkup?: {
    id: string;
    interests: string;
    group?: { id: string; name: string };
  };
};

const ReportCard = (
  { report, expanded, onToggle }: {
    report: Report;
    expanded: boolean;
    onToggle: () => void;
  },
) => {
  const th = t();
  const groupName = report.checkup?.group?.name ?? "Unknown";
  const date = new Date(report.createdAt);
  const conversationCount = (() => {
    try {
      return JSON.parse(report.conversationIds).length;
    } catch {
      return 0;
    }
  })();

  return (
    <div
      style={{
        background: th.cardBg,
        borderRadius: 8,
        border: `1px solid ${th.border}`,
        marginBottom: 8,
        overflow: "hidden",
      }}
    >
      <div
        onClick={onToggle}
        style={{
          padding: "12px 16px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <span style={{ fontWeight: 500, color: th.text, fontSize: 15 }}>
            {groupName}
          </span>
          <span style={{ color: th.textFaint, fontSize: 12, marginLeft: 12 }}>
            {date.toLocaleDateString()} {date.toLocaleTimeString()}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: th.textDim, fontSize: 12 }}>
            {conversationCount} conversation{conversationCount !== 1 ? "s" : ""}
          </span>
          <span style={{ color: th.textFaint, fontSize: 10 }}>
            {expanded ? "▲" : "▼"}
          </span>
        </div>
      </div>

      {expanded && (
        <div
          style={{
            padding: "0 16px 16px 16px",
            borderTop: `1px solid ${th.border}`,
          }}
        >
          <style
            dangerouslySetInnerHTML={{
              __html: `
.report-markdown {unicode-bidi:plaintext;text-align:start}
.report-markdown h1,.report-markdown h2,.report-markdown h3,.report-markdown h4{margin:20px 0 8px;font-weight:700;color:${th.textBright};unicode-bidi:plaintext;text-align:start}
.report-markdown h1{font-size:1.6em}
.report-markdown h2{font-size:1.4em}
.report-markdown h3{font-size:1.2em}
.report-markdown p{margin:8px 0;unicode-bidi:plaintext;text-align:start}
.report-markdown ul,.report-markdown ol{margin:8px 0;padding-left:24px}
.report-markdown li{margin:4px 0;unicode-bidi:plaintext;text-align:start}
.report-markdown li>ul,.report-markdown li>ol{margin:4px 0}
.report-markdown hr{border:none;border-top:1px solid ${th.border};margin:16px 0}
.report-markdown a{color:${th.primary};text-decoration:underline}
.report-markdown a:hover{opacity:0.8}
.report-markdown code{background:${th.inputBg};padding:1px 5px;border-radius:3px;font-size:0.9em}
.report-markdown pre{background:${th.inputBg};padding:12px;border-radius:6px;overflow-x:auto;margin:8px 0;direction:ltr;text-align:left;unicode-bidi:isolate}
.report-markdown pre code{background:none;padding:0}
.report-markdown blockquote{border-left:3px solid ${th.border};margin:8px 0;padding:4px 12px;color:${th.textMuted};unicode-bidi:plaintext;text-align:start}
`,
            }}
          />
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 12,
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            {report.viviConversationId && (
              <a
                href={`https://view-chat.com/conversations?conversationId=${report.viviConversationId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 4,
                  background: th.inputBg,
                  border: `1px solid ${th.border}`,
                  fontSize: 12,
                  color: th.primary,
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                👁 View Vivi's thought process
              </a>
            )}
            {report.checkup?.id && (
              <a
                href={`/checkups?checkupId=${
                  encodeURIComponent(report.checkup.id)
                }`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 4,
                  background: th.inputBg,
                  border: `1px solid ${th.border}`,
                  fontSize: 12,
                  color: th.primary,
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                ✏️ Edit report details
              </a>
            )}
          </div>
          <div
            className="report-markdown"
            style={{
              fontSize: 15,
              color: th.text,
              lineHeight: 1.6,
              paddingTop: 12,
            }}
            dangerouslySetInnerHTML={{
              __html: marked(report.content) as string,
            }}
          />
        </div>
      )}
    </div>
  );
};

export const ReportsPage = () => {
  const th = t();
  const { isLoading, error, data } = useQuery({ accounts: {} });

  const [reports, setReports] = useState<Report[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const accounts = data?.accounts || [];
  const accountSecret = accounts[0]?.secret;

  const loadReports = useCallback(() => {
    if (!accountSecret) return;
    setLoadingReports(true);
    setReportsError("");
    callApi("listReports", accountSecret, { limit: 50 })
      .then(
        (res: { ok?: boolean; reports?: Report[]; error?: string }) => {
          if (res.ok && res.reports) setReports(res.reports);
          else setReportsError(res.error || "Failed to load reports");
        },
      )
      .catch((e: Error) => setReportsError(e.message))
      .finally(() => setLoadingReports(false));
  }, [accountSecret]);

  useEffect(loadReports, [loadReports]);

  if (isLoading) return <Loading text="Loading" fullPage />;
  if (error) return <ErrorMessage error={error} />;

  if (empty(accounts)) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: th.textDim,
        }}
      >
        No accounts found. Create one in Accounts first.
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 24,
        background: th.pageBg,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 24,
        color: th.text,
      }}
    >
      <h1
        style={{
          fontSize: 24,
          fontWeight: 600,
          color: th.textBright,
          margin: 0,
        }}
      >
        Reports
      </h1>

      <div style={{ maxWidth: 700, width: "100%" }}>
        {loadingReports
          ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px 0",
              }}
            >
              <ClipLoader size={24} color={th.textFaint} />
            </div>
          )
          : reportsError
          ? (
            <div
              style={{ color: th.danger, fontSize: 13, textAlign: "center" }}
            >
              {reportsError}
            </div>
          )
          : empty(reports)
          ? (
            <div
              style={{
                textAlign: "center",
                color: th.textDim,
                padding: 40,
              }}
            >
              No reports yet. Reports are generated automatically by your
              checkups.
            </div>
          )
          : reports.map((report) => (
            <ReportCard
              key={report.id}
              report={report}
              expanded={expandedId === report.id}
              onToggle={() =>
                setExpandedId(
                  expandedId === report.id ? null : report.id,
                )}
            />
          ))}
      </div>
    </div>
  );
};
